import React, { useEffect, useState, useRef } from "react";

/**
 * Slim top-of-page animated progress bar that appears when at least one
 * axios request is in flight. Listens to the `busychange` custom event
 * dispatched from `lib/api.js`.
 *
 * Also switches the global cursor to "progress" via body[data-busy="1"] +
 * the CSS rules in App.css.
 *
 * A tiny 150 ms grace delay avoids flickering the bar for fast requests.
 */
export default function GlobalBusyIndicator() {
    const [visible, setVisible] = useState(false);
    const showTimer = useRef(null);
    const hideTimer = useRef(null);

    useEffect(() => {
        const onBusy = (e) => {
            const busy = !!e.detail?.busy;
            if (busy) {
                if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
                if (!showTimer.current) {
                    showTimer.current = setTimeout(() => {
                        setVisible(true);
                        showTimer.current = null;
                    }, 150); // grace: don't flash for very fast requests
                }
            } else {
                if (showTimer.current) { clearTimeout(showTimer.current); showTimer.current = null; }
                // Slight delay so the "finish" animation is perceivable
                hideTimer.current = setTimeout(() => {
                    setVisible(false);
                    hideTimer.current = null;
                }, 220);
            }
        };
        window.addEventListener("busychange", onBusy);
        return () => {
            window.removeEventListener("busychange", onBusy);
            if (showTimer.current) clearTimeout(showTimer.current);
            if (hideTimer.current) clearTimeout(hideTimer.current);
        };
    }, []);

    return (
        <div
            aria-hidden
            data-testid="global-busy-indicator"
            className={`fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        >
            <div className="h-full w-full bg-primary/15 overflow-hidden">
                <div className="h-full w-1/3 bg-primary busy-bar-slide rounded-full" />
            </div>
        </div>
    );
}
